import { useState } from 'react'
import { Alert } from '@/types'

interface AlertsWidgetProps {
  alerts: Alert[]
  className?: string
  maxItems?: number
  onMarkAsRead?: (alertId: string) => void
  onDismiss?: (alertId: string) => void
}

const severityColors = {
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  error: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border-red-200 dark:border-red-800',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border-red-200 dark:border-red-800'
}

const severityIcons = {
  info: 'ℹ️',
  warning: '⚠️',
  error: '❌',
  critical: '🚨'
}

const alertTypeLabels = {
  score_change: 'Score Change',
  band_change: 'Band Change',
  red_flag: 'Red Flag',
  data_update: 'Data Update',
  compliance_reminder: 'Compliance',
  system_notification: 'System'
}

export function AlertsWidget({ 
  alerts, 
  className = '', 
  maxItems = 5,
  onMarkAsRead,
  onDismiss 
}: AlertsWidgetProps) {
  const [filter, setFilter] = useState<'all' | 'unread' | 'actionRequired'>('unread')

  // Filter alerts based on selected filter
  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'unread' && alert.read) return false
    if (filter === 'actionRequired' && !alert.actionRequired) return false
    return true
  })

  // Sort by severity and date
  const sortedAlerts = filteredAlerts
    .sort((a, b) => {
      const severityOrder = { critical: 4, error: 3, warning: 2, info: 1 }
      const severityDiff = severityOrder[b.severity] - severityOrder[a.severity]
      if (severityDiff !== 0) return severityDiff
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
    .slice(0, maxItems)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d ago`
    
    return date.toLocaleDateString()
  }

  const unreadCount = alerts.filter(alert => !alert.read).length
  const actionRequiredCount = alerts.filter(alert => alert.actionRequired && !alert.read).length

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Alerts
            </h2>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {unreadCount} unread alert{unreadCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
              {unreadCount}
            </span>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'all'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            All ({alerts.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'unread'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter('actionRequired')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'actionRequired'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Action ({actionRequiredCount})
          </button>
        </div>

        {/* Alerts List */}
        {sortedAlerts.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-gray-400 dark:text-gray-500 text-xl">📬</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {filter === 'unread' ? 'No unread alerts' : 
               filter === 'actionRequired' ? 'No alerts requiring action' : 
               'No alerts'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`border rounded-lg p-3 transition-colors ${
                  alert.read 
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700' 
                    : severityColors[alert.severity]
                } ${!alert.read ? 'hover:bg-opacity-80' : 'hover:bg-gray-100 dark:hover:bg-gray-600'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="text-lg">
                      {severityIcons[alert.severity]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className={`text-sm font-medium ${
                          alert.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
                        }`}>
                          {alert.title}
                        </h3>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          {alertTypeLabels[alert.type]}
                        </span>
                        {alert.actionRequired && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                            Action Required
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${
                        alert.read ? 'text-gray-500 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'
                      } mb-2`}>
                        {alert.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatDate(alert.createdAt)}
                        </span>
                        {alert.actionUrl && (
                          <a
                            href={alert.actionUrl}
                            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                          >
                            Take Action →
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-1 ml-2">
                    {!alert.read && onMarkAsRead && (
                      <button
                        onClick={() => onMarkAsRead(alert.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Mark as read"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    )}
                    {onDismiss && (
                      <button
                        onClick={() => onDismiss(alert.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Dismiss"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All Link */}
        {alerts.length > maxItems && (
          <div className="mt-4 text-center">
            <a
              href="/alerts"
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
            >
              View all {alerts.length} alerts →
            </a>
          </div>
        )}
      </div>
    </div>
  )
}